import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import DropdownMenu from '../dropdown-menu/DropdownMenu';
import MediaDetailModal from '../media-preview/components/MediaDetailModal';
import MediaTranscriptionModal from '../media-preview/components/MediaTranscriptionModal';
import MediaPreviewModal from '../media-preview/components/MediaPreviewModal';
import { MediaPreviewHandle } from '../../types/media-preview';
import { AcrScanStatus, AcrScanResult } from '../../types/acr';
import { getAcrIconMeta } from '../../utils/acr';
import { buildAudioMenuItems } from '../../utils/audioMenu';
import { downloadMediaFile } from '../../utils/downloadFile';

import WaveformCanvas from './WaveformCanvas';
import { usePeaks } from './core/usePeaks';
import { useAudioTransport } from './core/useAudioTransport';
import { useVisibleOnce } from './core/useVisibleOnce';
import { estimatePeaksDuration, formatAudioTime } from './core/peaks';

/**
 * オプションモード（Messenger 内の比較試聴グループ）。
 *
 * 1 メッセージに複数のオーディオ添付があるとき、個別プレイヤーの縦積みではなく
 * 1 枚のグループカードにまとめ、A / B / C… のオプションとして聴き比べられるようにする。
 *
 * 仕様（v2）:
 * - 発動条件: 自動（フォルダ外のオーディオ添付が 2 件以上）。呼び出し側（MessageItem）が判定する
 * - 再生は排他（グローバルアービター）。別オプションの再生開始で前の再生は止まる
 * - A / B / C バッジが再生ボタンを兼ねる: 停止中はレター表示、クリックで**頭出し再生**
 *   （同時にそのオプションが選択状態になる）、再生中は停止アイコンに変わる
 * - 波形クリック（シーク）でもそのオプションが選択される。シーク後の Space /
 *   再生ハンドル経由の再生は従来どおりその位置から
 * - 行クリックでも選択をトグルできる（再生は伴わない）
 * - 「◯案で返信」チップは廃止（Messenger 内レスポンスの扱いは別途検討）
 */

export interface AudioOption {
  /** ファイル ID（peaks キャッシュキー・ハンドル登録キーを兼ねる） */
  id: string;
  src: string;
  fileName?: string;
  /** Lambda 事前計算の peaks JSON URL（無ければ可視化後に decode フォールバック） */
  waveformUrl?: string | null;
  downloadUrl?: string;
  // ACR 楽曲認識
  acrScanStatus?: AcrScanStatus;
  acrScanResult?: AcrScanResult | null;
  acrScannedAt?: string | null;
  /** 行の下に差し込む追加コンテンツ（ダウンロード既読アバター等） */
  footer?: React.ReactNode;
}

export interface AudioOptionGroupProps {
  options: AudioOption[];
  /** ダウンロード実行後の記録コールバック（既読登録等） */
  onDownloaded?: (_option: AudioOption) => void;
  onPlayStateChange?: (_playing: boolean) => void;
  /** 再生制御ハンドルの登録（メッセージの再生ボタン連携等。null で解除） */
  registerHandle?: (_optionId: string, _handle: MediaPreviewHandle | null) => void;
  showMenu?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const WAVEFORM_HEIGHT = 24;

/** オプションレター（A, B, C, ... Z, AA, AB...） */
function optionLabel(index: number): string {
  let label = '';
  let n = index;
  do {
    label = String.fromCharCode(65 + (n % 26)) + label;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return label;
}

// =============================================================================
// Styled Components
// =============================================================================

const GroupCard = styled.div`
  width: 100%;
  padding: 8px 12px;
  background-color: var(--soremo-background);
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const OptionContainer = styled.div<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 8px;
  margin: 0 -8px;
  border-radius: 6px;
  cursor: pointer;
  background-color: ${props => props.$selected ? 'rgba(0, 154, 206, 0.06)' : 'transparent'};
  box-shadow: ${props => props.$selected ? 'inset 0 0 0 1px rgba(0, 154, 206, 0.35)' : 'none'};
  transition: background-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    background-color: ${props => props.$selected ? 'rgba(0, 154, 206, 0.10)' : 'rgba(0, 0, 0, 0.03)'};
  }
`;

const OptionMainRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

/**
 * A / B / C バッジ兼再生ボタン。
 * 停止中はレター入りの丸バッジ（選択中は青）、再生中は停止アイコンを表示する。
 */
const OptionPlayBadge = styled.button<{ $selected: boolean; $isPlaying: boolean }>`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  user-select: none;
  cursor: pointer;
  color: ${props =>
    props.$isPlaying
      ? 'var(--soremo-blue)'
      : props.$selected
        ? '#fff'
        : 'var(--soremo-gray)'};
  background-color: ${props =>
    props.$isPlaying
      ? 'transparent'
      : props.$selected
        ? 'var(--soremo-blue)'
        : 'var(--soremo-border)'};
  transition: color 0.15s ease, background-color 0.15s ease;

  .material-symbols-rounded {
    font-size: 24px;
    line-height: 1;
  }

  &:hover {
    scale: 1.1;
  }
`;

const WaveArea = styled.div`
  flex: 1;
  min-width: 0;
`;

const TimeText = styled.span.attrs({
  className: 'bodytext-11',
})`
  flex-shrink: 0;
  color: var(--soremo-light-gray);
  line-height: 1;
  user-select: none;
  font-variant-numeric: tabular-nums;
`;

const OptionFooterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  /* バッジ + gap 分だけインデントして波形の開始位置に揃える */
  padding-left: 32px;
`;

const OptionFileName = styled.span.attrs({
  className: 'bodytext-11 u-line-height-150',
})`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--soremo-light-gray);
`;

const AcrBadge = styled.span`
  font-size: 18px;
  flex-shrink: 0;
  line-height: 1;
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }
`;

// =============================================================================
// オプション行
// =============================================================================

interface AudioOptionRowProps {
  option: AudioOption;
  label: string;
  selected: boolean;
  /** 行クリック（選択のトグル） */
  onSelect: () => void;
  /** 再生開始時の選択（トグルではなく選択のみ） */
  onPlaySelect: () => void;
  showMenu: boolean;
  onPlayStateChange?: (_playing: boolean) => void;
  registerHandle?: (_optionId: string, _handle: MediaPreviewHandle | null) => void;
  /** メニュー「プレビュー」（モーダルで波形プレビューを開く） */
  onOpenPreview: () => void;
  onDownloaded?: (_option: AudioOption) => void;
}

const AudioOptionRow = memo<AudioOptionRowProps>(({
  option,
  label,
  selected,
  onSelect,
  onPlaySelect,
  showMenu,
  onPlayStateChange,
  registerHandle,
  onOpenPreview,
  onDownloaded,
}) => {
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showTranscriptionModal, setShowTranscriptionModal] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);

  // 波形 JSON は即 fetch。無い / 失敗した場合は画面内に入ってから decode フォールバック
  const visible = useVisibleOnce(rowRef);
  const { peaks } = usePeaks({
    cacheKey: option.id,
    waveformUrl: option.waveformUrl,
    fallbackSrc: visible ? option.src : undefined,
    enabled: !!option.waveformUrl || visible,
  });

  const transport = useAudioTransport({
    src: option.src,
    preload: 'metadata',
    initialDuration: estimatePeaksDuration(peaks),
    onPlayStateChange,
  });
  const { isPlaying, currentTime, duration } = transport;

  // 再生前は総尺、再生開始後は現在秒数を表示する
  const displayTime = isPlaying || currentTime > 0 ? currentTime : duration;

  // --- 再生制御ハンドルの登録（transport は毎レンダー参照が変わるため ref 経由） ---
  const transportRef = useRef(transport);
  transportRef.current = transport;
  useEffect(() => {
    if (!registerHandle) return;
    const handle: MediaPreviewHandle = {
      play: async () => transportRef.current.play(),
      pause: () => transportRef.current.pause(),
      seek: (seconds: number) => transportRef.current.seekBy(seconds),
      seekTo: (seconds: number) => transportRef.current.seekTo(seconds),
      getCurrentTime: () => transportRef.current.getAudioElement()?.currentTime ?? 0,
      getDuration: () => transportRef.current.getAudioElement()?.duration ?? 0,
      clickToggle: () => transportRef.current.toggle(),
      getMediaElement: () => transportRef.current.getAudioElement(),
    };
    registerHandle(option.id, handle);
    return () => registerHandle(option.id, null);
  }, [registerHandle, option.id]);

  const handleDetailClick = useCallback(() => {
    setShowDetailModal(true);
  }, []);

  const handleDownloadClick = useCallback(() => {
    if (!downloadMediaFile({
      downloadUrl: option.downloadUrl,
      src: option.src,
      fileName: option.fileName,
      fallbackName: 'audio',
    })) return;
    onDownloaded?.(option);
  }, [option, onDownloaded]);

  const handleTranscribeClick = useCallback(() => {
    setShowTranscriptionModal(true);
  }, []);

  const menuItems = useMemo(() => buildAudioMenuItems({
    onPreview: onOpenPreview,
    onDetailInfo: handleDetailClick,
    onDownload: handleDownloadClick,
    onTranscribe: handleTranscribeClick,
    acrScanStatus: option.acrScanStatus,
  }), [onOpenPreview, handleDetailClick, handleDownloadClick, handleTranscribeClick, option.acrScanStatus]);

  const acrIcon = getAcrIconMeta(option.acrScanStatus);

  return (
    <>
      <OptionContainer
        ref={rowRef}
        $selected={selected}
        onClick={() => {
          // 選択された行はスペースキー再生のターゲットにもなる
          transport.markActive();
          onSelect();
        }}
        role="option"
        aria-selected={selected}
      >
        <OptionMainRow>
          <OptionPlayBadge
            type="button"
            $selected={selected}
            $isPlaying={isPlaying}
            aria-label={isPlaying ? `${label}案を停止` : `${label}案を頭から再生`}
            onClick={e => {
              e.stopPropagation();
              if (isPlaying) {
                transport.pause();
              } else {
                // バッジからの再生は必ず頭出し + そのオプションを選択。
                // （シーク位置からの再生は波形クリック → Space / ハンドル経由で行う）
                onPlaySelect();
                transport.seekTo(0);
                transport.play();
              }
            }}
          >
            {isPlaying ? (
              <span className="material-symbols-rounded">pause_circle</span>
            ) : (
              label
            )}
          </OptionPlayBadge>
          <WaveArea onClick={e => e.stopPropagation()}>
            <WaveformCanvas
              peaks={peaks}
              currentTime={currentTime}
              duration={duration}
              height={WAVEFORM_HEIGHT}
              channelMode="mono"
              onSeek={seconds => {
                // 再生位置の指定 = そのオプションに意識が向いている状態なので選択も切り替える
                onPlaySelect();
                transport.seekTo(seconds);
              }}
              showHoverIndicator
            />
          </WaveArea>
          <TimeText>{formatAudioTime(displayTime)}</TimeText>
        </OptionMainRow>
        <OptionFooterRow>
          <OptionFileName>{option.fileName}</OptionFileName>
          {acrIcon.show && (
            <AcrBadge
              className="material-symbols-rounded"
              style={{ color: acrIcon.color }}
              onClick={e => {
                e.stopPropagation();
                handleDetailClick();
              }}
            >
              error
            </AcrBadge>
          )}
          {showMenu && (
            <span onClick={e => e.stopPropagation()}>
              <DropdownMenu items={menuItems} />
            </span>
          )}
        </OptionFooterRow>
        {option.footer}
      </OptionContainer>
      {showDetailModal && (
        <MediaDetailModal
          onClose={() => setShowDetailModal(false)}
          fileName={option.fileName}
          acrScanResult={option.acrScanResult}
          fileId={option.id}
          acrScanStatus={option.acrScanStatus}
          acrScannedAt={option.acrScannedAt}
        />
      )}
      {showTranscriptionModal && (
        <MediaTranscriptionModal
          fileId={option.id}
          fileName={option.fileName}
          onClose={() => setShowTranscriptionModal(false)}
          onSeek={seconds => transport.seekTo(seconds)}
        />
      )}
    </>
  );
});

AudioOptionRow.displayName = 'AudioOptionRow';

// =============================================================================
// グループ本体
// =============================================================================

const AudioOptionGroup: React.FC<AudioOptionGroupProps> = ({
  options,
  onDownloaded,
  onPlayStateChange,
  registerHandle,
  showMenu = true,
  className,
  style,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [previewOption, setPreviewOption] = useState<AudioOption | null>(null);

  return (
    <>
      <GroupCard className={className} style={style}>
        {options.map((option, index) => (
          <AudioOptionRow
            key={option.id}
            option={option}
            label={optionLabel(index)}
            selected={option.id === selectedId}
            onSelect={() => setSelectedId(prev => (prev === option.id ? null : option.id))}
            onPlaySelect={() => setSelectedId(option.id)}
            showMenu={showMenu}
            onPlayStateChange={onPlayStateChange}
            registerHandle={registerHandle}
            onOpenPreview={() => setPreviewOption(option)}
            onDownloaded={onDownloaded}
          />
        ))}
      </GroupCard>
      {previewOption && (
        <MediaPreviewModal
          src={previewOption.src}
          mediaType="audio"
          onClose={() => setPreviewOption(null)}
          fileName={previewOption.fileName}
          waveformUrl={previewOption.waveformUrl ?? undefined}
          fileId={previewOption.id}
          acrScanStatus={previewOption.acrScanStatus}
          acrScanResult={previewOption.acrScanResult}
          acrScannedAt={previewOption.acrScannedAt}
          downloadUrl={previewOption.downloadUrl}
        />
      )}
    </>
  );
};

export default AudioOptionGroup;
